<template>
  <div :class="rootClass">
    <WarRoomVoteButton
      :is-disabled="isDisabled"
      :is-upvoted="isUpvoted"
      :is-upvote="true"
      data-test="war_room_rating_up"
      @click="handleUpVote"
    >
      <ArrowIcon class="war-room-vote__icon" />
    </WarRoomVoteButton>
    <div :class="counterClasses" data-test="war_room_rating_count">
      {{ rating }}
    </div>
    <WarRoomVoteButton
      :is-downvoted="isDownvoted"
      :is-disabled="isDisabled"
      :is-downvote="true"
      data-test="war_room_rating_down"
      @click="handleDownVote"
    >
      <ArrowIcon :class="arrowIconClassesDownvoted" />
    </WarRoomVoteButton>
  </div>
</template>

<script lang="ts">
import { defineComponent } from '@nuxtjs/composition-api';
import type { PropOptions } from 'vue';
import { mapActions } from 'vuex';
import WarRoomVoteButton from '@/components/partials/war-room/WarRoomVote/WarRoomVoteButton.vue';
import ArrowIcon from '@/components/partials/war-room/WarRoomVote/images/arrow.vue.svg';
import warRoomSignUpMixin from '@/components/partials/war-room/WarRoomSignUp/warRoomSignUpMixin';
import { votableTypes } from '@/components/partials/war-room/constants';
import type { IPost, IPostComment } from '@/types/community';

export default defineComponent({
  name: 'WarRoomPostVote',
  components: {
    ArrowIcon,
    WarRoomVoteButton,
  },
  mixins: [warRoomSignUpMixin],
  props: {
    post: {
      type: Object,
      required: true,
    } as PropOptions<IPost>,
    comment: {
      type: Object,
      default: null,
    } as PropOptions<IPostComment | null>,
  },
  computed: {
    entity() {
      return this.comment || this.post;
    },
    isDisabled() {
      return !this.comment || this.comment.is_deleted;
    },
    arrowIconClassesDownvoted() {
      return {
        'war-room-vote__icon': true,
        'war-room-vote__icon--reversed': true,
      };
    },
    isUpvoted() {
      return this.entity && this.entity.current_vote === 1;
    },
    isDownvoted() {
      return this.entity && this.entity.current_vote === -1;
    },
    counterClasses() {
      return {
        'war-room-vote__count': true,
        'war-room-vote__count--upvoted': this.isUpvoted,
        'war-room-vote__count--downvoted': this.isDownvoted,
      };
    },
    rootClass() {
      return {
        'war-room-vote': true,
        'war-room-vote--deleted': this.comment?.is_deleted,
      };
    },
    rating() {
      return this.comment?.is_deleted ? '–' : this.entity?.rating;
    },
  },
  methods: {
    ...mapActions('warRoom', ['addVote', 'removeVote']),
    handleUpVote() {
      if (!this.$auth.loggedIn) return this.openWarRoomSignUpModal();

      this.$auth.loggedIn && this.isUpvoted ? this.handleRemoveVote() : this.handleAddVote(1);
    },
    handleDownVote() {
      if (!this.$auth.loggedIn) return this.openWarRoomSignUpModal();

      this.$auth.loggedIn && this.isDownvoted ? this.handleRemoveVote() : this.handleAddVote(-1);
    },
    handleAddVote(value: number) {
      const pageAndElement = this.comment ? '1-post__comment' : 'post';
      this.$analytics.logEvent(`war-room__${pageAndElement}__rating-set`, {
        value,
      });

      this.changeRating(this.addVote, value);
    },
    handleRemoveVote() {
      const pageAndElement = this.comment ? '1-post__comment' : 'post';
      this.$analytics.logEvent(`war-room__${pageAndElement}__rating-unset`);

      this.changeRating(this.removeVote);
    },
    async changeRating(action: Function, value?: number) {
      const type = this.comment ? votableTypes.COMMENT : votableTypes.POST;
      let votable;

      if (this.comment && !this.comment.is_deleted) {
        votable = this.comment;
      } else {
        votable = this.post;
      }

      try {
        await action({ votable, type, value });
      } catch (e) {
        this.$sentryUtils.error(e);

        if (e instanceof Error) {
          this.$notice.error(e);
        }
      }
    },
  },
});
</script>

<style scoped lang="scss">
.war-room-vote {
  $this: &;

  @include flex-center;

  flex-direction: column;
  justify-content: flex-start;
  color: var(--theme-color-text);

  &--deleted {
    #{$this}__count {
      color: var(--theme-color-text);
      opacity: 0.3;
    }
  }

  &__icon {
    color: inherit;
    width: 16px;
    height: 16px;

    &--reversed {
      transform: rotate(180deg);
    }
  }

  &__count {
    @include subhead;
    @include semibold;

    color: inherit;
    margin: 2px 0;

    &--upvoted {
      color: var(--blue-60);
    }

    &--downvoted {
      color: var(--red-50);
    }
  }
}
</style>
